import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '@/components/Modal';
import { feedbackApi, type FeedbackItem, type StoppedFollowers, type VisibilityResult } from '@/lib/apiClient';
import { BOARD_KEY, CATEGORY_LABEL, MINE_KEY, firstLine, fmtWhen, statusLabel } from '@/lib/feedbackMine';
import {
  BOARD_CLOSED_WINDOW_DAYS, SHOW_BATCH_MAX, closedPastTheWindow, describeStopped, describeVisibility, lateReplies, mayHaveBeenApplied, repliesLoaded, stoppedHeading, toggleIn,
  visibilityHeading, type VisibilityNote,
} from './feedbackBoard';
import { plural } from '@/lib/plural';

/**
 * Its own `fbb-` styles, declared here: a class in this app is live only while the component that declares it is mounted (there are
 * no CSS Modules), so these dialogs do not lean on the Feedback tab's rules.
 */
const css = `
  .fbb-warn { margin: 0 0 14px; padding: 10px 12px; border: 1px solid var(--ew-warning); border-radius: var(--ew-r-md); background: var(--ew-n-50); font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbb-total { margin: 0 0 12px; font: var(--ew-fw-semibold) 13px/1.3 var(--ew-font); color: var(--ew-dark); }
  .fbb-list { display: flex; flex-direction: column; gap: 10px; }
  .fbb-card { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 10px 12px; background: #fff; }
  .fbb-card.off { opacity: 0.55; background: var(--ew-n-50); }
  .fbb-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; font: var(--ew-fw-medium) 11.5px/1.2 var(--ew-font); color: var(--ew-n-500); }
  .fbb-head label { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; color: var(--ew-dark); font-weight: var(--ew-fw-semibold); }
  .fbb-spacer { flex: 1; }
  .fbb-msg { margin: 0; white-space: pre-wrap; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbb-replies { margin: 8px 0 0; padding-top: 8px; border-top: 1px solid var(--ew-n-100); display: flex; flex-direction: column; gap: 6px; }
  .fbb-reply { padding: 6px 10px; border-left: 3px solid var(--ew-green); background: var(--ew-n-50); border-radius: var(--ew-r-sm); }
  .fbb-reply-head { font: var(--ew-fw-medium) 11px/1.2 var(--ew-font); color: var(--ew-n-500); margin-bottom: 3px; }
  .fbb-reply-head b { color: var(--ew-dark); }
  .fbb-reply p { margin: 0; white-space: pre-wrap; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbb-muted { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .fbb-cant { margin: 8px 0 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-danger); }
  .fbb-result { margin: 0; padding: 10px 12px; border-radius: var(--ew-r-md); border: 1px solid var(--ew-n-200); font: var(--ew-fw-regular) 13px/1.5 var(--ew-font); color: var(--ew-dark); }
  .fbb-result.warn { border-color: var(--ew-warning); }
  .fbb-result.error { border-color: var(--ew-danger); color: var(--ew-danger); }
  .fbb-problems { margin: 10px 0 0; padding-left: 18px; font: var(--ew-fw-regular) 12.5px/1.5 var(--ew-font); color: var(--ew-dark); }
  .fbb-opt { display: flex; gap: 8px; align-items: flex-start; margin: 12px 0 0; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); cursor: pointer; }
  .fbb-opt input { margin-top: 3px; }
  .fbb-danger { margin: 8px 0 0 26px; font: var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); color: var(--ew-danger); }
`;

/** What an item that did not change was, in a line an admin can find in the list: its number and the start of what it says. */
const problemLine = (r: VisibilityResult, items: readonly FeedbackItem[]): string => {
  const it = items.find((i) => i.id === r.id);
  const what = it ? `“${firstLine(it.message, 60)}”` : 'an item that is no longer in the list';
  const why: Record<VisibilityResult['outcome'], string> = {
    changed: 'changed', unchanged: 'already so',
    refused: r.reason === 'private' ? 'the sender asked to keep it private' : r.reason === 'merged' ? 'it was combined into another request' : 'a state this screen does not know',
    not_found: 'no longer exists', conflict: 'changed by someone else', error: 'could not be changed',
  };
  return `#${r.id} ${what}: ${why[r.outcome]}`;
};

function ResultBlock({ note, results, items, late = 0 }: { note: VisibilityNote; results: readonly VisibilityResult[]; items: readonly FeedbackItem[]; late?: number }) {
  const problems = results.filter((r) => r.outcome !== 'changed' && r.outcome !== 'unchanged');
  return (
    <>
      <p role="status" className={`fbb-result${note.tone === 'ok' && !late ? '' : ` ${note.tone === 'ok' ? 'warn' : note.tone}`}`}>
        {note.text}
        {late > 0 && ` ${plural(late, 'reply was', 'replies were')} added after this preview was opened, and went public with the item without being in it. Look at the board.`}
      </p>
      {problems.length > 0 && (
        <ul className="fbb-problems">{problems.map((r) => <li key={r.id}>{problemLine(r, items)}</li>)}</ul>
      )}
    </>
  );
}

/** After a request that failed: it may have been applied before it did, so the list (refreshed now) is where to see. */
const FAILED_SO_LOOK = 'If any of it was applied before this failed, the list now shows it.';

/**
 * A failed request, in the API's own words. ⚠️ THE HEDGE ONLY WHERE IT IS TRUE: "if any of it was applied before this failed" follows a failure
 * that may have been applied (a dropped connection, a 5xx), never the API's own refusals (a 403 for the read-only "view as" mode, a 409 "nothing
 * was stopped"), which it would contradict.
 */
function FailureNote({ error }: { error: unknown }) {
  return <p className="fbb-cant" role="alert">{(error as Error).message}{mayHaveBeenApplied(error) ? ` ${FAILED_SO_LOOK}` : ''}</p>;
}

/** Whatever a show or hide changed is read from three places, so all three are refreshed. */
function useRefreshAfterChange() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    // Followers may have been removed, and the board (the drawer's Everyone's feedback tab and its suggestions read this key) lists what is public.
    // The drawer also looks again whenever it is opened, so this only keeps what is cached from outliving a Show or a Hide.
    void qc.invalidateQueries({ queryKey: MINE_KEY });
    void qc.invalidateQueries({ queryKey: BOARD_KEY });
  };
}

// ─── Show on board ─────────────────────────────────────────────────────────────────

/**
 * The preview before items go on the board: EVERY message in full and EVERY reply on it, with the replies' authors' names, because
 * showing an item makes its replies public with it, including ones written when only the sender could read them. Nothing is shown
 * until the admin confirms, and any item can be left out from here.
 *
 * ⚠️ AN ITEM WHOSE REPLIES COULD NOT BE LOADED CANNOT BE SHOWN. The list says "replies unavailable" when the conversation could not be
 * read, and then there is nothing to preview: publishing it would publish replies nobody has seen. It is left out, with the reason.
 */
export function ShowOnBoardDialog({ items, onClose, onDone }: { items: FeedbackItem[]; onClose: () => void; onDone: () => void }) {
  const refresh = useRefreshAfterChange();
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());
  const [results, setResults] = useState<VisibilityResult[] | null>(null);
  const [late, setLate] = useState(0);

  const included = items.filter((i) => repliesLoaded(i) && !excluded.has(i.id));
  const replies = included.reduce((n, i) => n + (i.replies?.length ?? 0), 0);
  const tooMany = included.length > SHOW_BATCH_MAX;

  const run = useMutation({
    mutationFn: (ids: number[]) => feedbackApi.setVisibility(ids, 'public'),
    onSuccess: (r, ids) => {
      // A reply added since the preview was opened is public all the same: say how many, rather than leave "shown" to read as "checked".
      setLate(lateReplies(r, new Map(items.filter((i) => ids.includes(i.id)).map((i) => [i.id, i.replies?.length ?? 0]))));
      setResults(r);
    },
    // ⚠️ SETTLED, not only success: the API applies the items one at a time, so a request that fails part-way (a dropped connection, a
    // gateway timeout) may have changed some of them, and a list that was not refreshed would still offer to show what is already shown.
    onSettled: refresh,
  });
  const toggle = (id: number) => setExcluded((prev) => toggleIn(prev, id));
  const finish = () => { if (results) onDone(); onClose(); };

  return (
    <Modal
      isOpen
      onClose={run.isPending ? () => undefined : finish}
      title={results ? visibilityHeading(results, 'public') : 'Show on the board?'}
      maxWidth={760}
      closeOnBackdropClick={false}
      footer={results ? (
        <button className="btn btn-primary" onClick={finish}>Close</button>
      ) : (
        <>
          <button className="btn btn-secondary" disabled={run.isPending} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={included.length === 0 || tooMany || run.isPending}
            onClick={() => run.mutate(included.map((i) => i.id))}
          >
            {run.isPending ? 'Showing…' : `Show ${included.length} on the board`}
          </button>
        </>
      )}
    >
      <style>{css}</style>
      {results ? (
        <ResultBlock note={describeVisibility(results, 'public')} results={results} items={items} late={late} />
      ) : (
        <>
          <p className="fbb-warn" role="note">
            <strong>Everyone signed in will be able to read these messages and every reply on them, with the replies’ authors’ names.</strong>{' '}
            The sender’s name is never shown. Read each one below, and untick any that should stay between the sender and the admins.
          </p>
          <p className="fbb-total" aria-live="polite">
            Showing {plural(included.length, 'item', 'items')}, with {plural(replies, 'reply', 'replies')}.
          </p>
          {tooMany && <p className="fbb-cant" role="alert">That is more than {SHOW_BATCH_MAX}, the most that can be shown at once. Untick some, then show the rest next.</p>}
          <div className="fbb-list">
            {items.map((i) => {
              const canPreview = repliesLoaded(i);
              const on = canPreview && !excluded.has(i.id);
              const rs = i.replies ?? [];
              return (
                <div key={i.id} className={`fbb-card${on ? '' : ' off'}`}>
                  <div className="fbb-head">
                    <label>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!canPreview || run.isPending}
                        aria-label={`Show item ${i.id} on the board`}
                        onChange={() => toggle(i.id)}
                      />
                      Show this one
                    </label>
                    <span>{CATEGORY_LABEL[i.category] ?? i.category}</span>
                    <span>{statusLabel(i.status, i.closedReason)}</span>
                    {i.boardVisibility === 'hidden' && <strong style={{ color: 'var(--ew-warning)' }}>Hidden earlier</strong>}
                    <span className="fbb-spacer" />
                    <span>{fmtWhen(i.createdAt)}</span>
                  </div>
                  <p className="fbb-msg">{i.message}</p>
                  {closedPastTheWindow(i) && (
                    <p className="fbb-muted" style={{ marginTop: 6 }}>
                      Closed more than {BOARD_CLOSED_WINDOW_DAYS} days ago. The board lists a closed item for {BOARD_CLOSED_WINDOW_DAYS} days, so nobody will see this one there unless it is reopened.
                    </p>
                  )}
                  {canPreview ? (
                    rs.length > 0 ? (
                      <div className="fbb-replies" aria-label={`Replies on item ${i.id}`}>
                        {rs.map((r) => (
                          <div key={r.id} className="fbb-reply">
                            <div className="fbb-reply-head"><b>{r.authorName ?? 'Unknown'}</b> · {fmtWhen(r.createdAt)}</div>
                            <p>{r.body}</p>
                          </div>
                        ))}
                      </div>
                    ) : <p className="fbb-muted" style={{ marginTop: 8 }}>No replies.</p>
                  ) : (
                    <p className="fbb-cant">Its replies could not be loaded, so they cannot be previewed, and it cannot be shown from here.</p>
                  )}
                </div>
              );
            })}
          </div>
          {run.isError && <FailureNote error={run.error} />}
        </>
      )}
    </Modal>
  );
}

// ─── Hide ──────────────────────────────────────────────────────────────────────────

/**
 * Taking an item off the board, or marking one nobody has reviewed as "not for the board" (the same write: it becomes `hidden`, which
 * is how the To review list is drained of items that should never be shared). By DEFAULT the people who already follow it keep it: it stays under their My feedback and they keep
 * being emailed, because they asked to be told and would otherwise stop hearing without knowing why. "Also stop telling them" is an
 * opt-in, off by default, for an item hidden because its text should not be read any more; it removes every follower but the sender,
 * and cannot be undone.
 *
 * The number of people it affects is `otherFollowerCount`, which the API counts exactly (the followers who are not the sender and whose
 * accounts can still be told), so the opt-in is offered when there is somebody to opt out and the number is the one the answer will give.
 */
export function HideDialog({ item, onClose }: { item: FeedbackItem; onClose: () => void }) {
  const refresh = useRefreshAfterChange();
  const [stopTelling, setStopTelling] = useState(false);
  const [asked, setAsked] = useState(false);
  const [results, setResults] = useState<VisibilityResult[] | null>(null);
  const fromQueue = item.boardVisibility === 'unreviewed';

  const others = item.otherFollowerCount;
  // The opt-in is offered when somebody besides the sender follows it, and not when the count could not be read.
  const offer = others != null && others > 0;

  // ⚠️ WHAT WAS ASKED IS THE MUTATION'S VARIABLE, not the checkbox's state when the answer arrives: the answer is worded by what was
  // SENT, and a box ticked or unticked while the request was in flight must not change what the answer says happened.
  const run = useMutation({
    mutationFn: (removeFollowers: boolean) => feedbackApi.setVisibility([item.id], 'hidden', { removeFollowers }),
    onSuccess: (r, removeFollowers) => { setAsked(removeFollowers); setResults(r); },
    onSettled: refresh, // see ShowOnBoardDialog: a failure may still have changed the item
  });

  return (
    <Modal
      isOpen
      onClose={run.isPending ? () => undefined : onClose}
      title={results ? visibilityHeading(results, 'hidden', fromQueue) : fromQueue ? 'Keep it off the board?' : 'Take it off the board?'}
      maxWidth={560}
      closeOnBackdropClick={false}
      footer={results ? (
        <button className="btn btn-primary" onClick={onClose}>Close</button>
      ) : (
        <>
          <button className="btn btn-secondary" disabled={run.isPending} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={run.isPending} onClick={() => run.mutate(offer && stopTelling)}>
            {run.isPending ? 'Hiding…' : offer && stopTelling ? 'Hide and stop telling them' : fromQueue ? 'Keep it off the board' : 'Hide'}
          </button>
        </>
      )}
    >
      <style>{css}</style>
      {results ? (
        <ResultBlock note={describeVisibility(results, 'hidden', { removeFollowers: asked })} results={results} items={[item]} />
      ) : (
        <>
          <p className="fbb-msg" style={{ marginBottom: 12 }}>“{firstLine(item.message, 160)}”</p>
          <p className="fbb-muted" style={{ marginBottom: 4 }}>
            {fromQueue
              ? 'This marks it as reviewed and not for the board. Only the admins and the person who sent it can read it. The item is not deleted, and it can be shown later.'
              : 'Hiding takes it off the board for everyone. The item is not deleted, and it can be shown again.'}
          </p>
          {others == null ? (
            <p className="fbb-muted">Who follows it could not be loaded, so the people who do will keep it.</p>
          ) : others > 0 ? (
            <p className="fbb-muted">
              {plural(others, 'person follows', 'people follow')} it besides the person who sent it. <strong>By default they keep it:</strong>{' '}
              it stays under their My feedback and they keep being emailed about it.
            </p>
          ) : (
            <p className="fbb-muted">Nobody but the person who sent it follows it.</p>
          )}
          {offer && (
            <>
              <label className="fbb-opt">
                <input type="checkbox" checked={stopTelling} disabled={run.isPending} onChange={(e) => setStopTelling(e.target.checked)} />
                <span>Also stop telling them</span>
              </label>
              <p className="fbb-muted" style={{ margin: '2px 0 0 26px' }}>Everyone who follows it except the person who sent it. You can also do this later, from the Board column, while it stays hidden.</p>
              {stopTelling && (
                <p className="fbb-danger" role="alert">
                  They will not be told about it again, and it will leave their My feedback. This cannot be undone: showing it again does not put them back. If this cannot be done, the item is not hidden either.
                </p>
              )}
            </>
          )}
          {run.isError && <FailureNote error={run.error} />}
        </>
      )}
    </Modal>
  );
}

// ─── Stop telling the followers of an item that is already hidden ─────────────────────

/**
 * For an item that was hidden WITHOUT choosing "also stop telling them", whose followers therefore keep it under My feedback and keep being
 * emailed. It is the same removal as the opt-in on Hide, done later, so the admin no longer has to show the item again (publishing it, with
 * every reply) in order to hide it a second time. It removes everyone who follows it except the person who sent it; they are not told, and it
 * cannot be undone: showing the item again does not put them back.
 *
 * Offered only for a HIDDEN item with somebody besides the sender following it (`canStopTelling`), and the number is the API's own count of
 * the people who can still be told (`otherFollowerCount`). The API refuses any other item, so a list that was stale says so here.
 */
export function StopTellingDialog({ item, onClose }: { item: FeedbackItem; onClose: () => void }) {
  const refresh = useRefreshAfterChange();
  const [result, setResult] = useState<StoppedFollowers | null>(null);
  const run = useMutation({
    mutationFn: () => feedbackApi.stopTellingFollowers(item.id),
    onSuccess: setResult,
    onSettled: refresh, // a failure may still have changed the list
  });
  const others = item.otherFollowerCount ?? 0;
  const note = result ? describeStopped(result) : null;

  return (
    <Modal
      isOpen
      onClose={run.isPending ? () => undefined : onClose}
      title={result ? stoppedHeading(result) : 'Stop telling the people who follow it?'}
      maxWidth={560}
      closeOnBackdropClick={false}
      footer={result ? (
        <button className="btn btn-primary" onClick={onClose}>Close</button>
      ) : (
        <>
          <button className="btn btn-secondary" disabled={run.isPending} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Stopping…' : 'Stop telling them'}
          </button>
        </>
      )}
    >
      <style>{css}</style>
      {note ? (
        <p role="status" className={`fbb-result${note.tone === 'ok' ? '' : ` ${note.tone}`}`}>{note.text}</p>
      ) : (
        <>
          <p className="fbb-msg" style={{ marginBottom: 12 }}>“{firstLine(item.message, 160)}”</p>
          <p className="fbb-muted" style={{ marginBottom: 4 }}>
            It is hidden from the board, but {plural(others, 'person follows', 'people follow')} it besides the person who sent it.{' '}
            <strong>They keep it</strong> under their My feedback and keep being emailed about it.
          </p>
          <p className="fbb-danger" style={{ marginLeft: 0 }}>
            They will not be told about it again, and it will leave their My feedback. This cannot be undone: showing the item again does not put
            them back. The person who sent it is not affected.
          </p>
          {run.isError && <FailureNote error={run.error} />}
        </>
      )}
    </Modal>
  );
}
